/**
 * Calls the query classification endpoint.
 * @param {string} queryText The user's search query.
 * @returns {Promise<string | null>} The predicted class name (e.g., 'default', 'legal_question') or null if an error occurs.
 */


export const classifyQuery = async (queryText) => {
  const endpointUrl = import.meta.env.VITE_CLASSIFICATION_ENDPOINT_URL;
  console.log("[classifyQuery] Endpoint URL:", endpointUrl); 

  if (!endpointUrl) {
    console.error("[classifyQuery] ERROR: Classification endpoint URL is not defined!");
    return 'default'; // Default fallback if URL is missing
  }

  if (!queryText || queryText.trim() === "") {
    console.log("[classifyQuery] Query is empty, returning 'default'."); 
    return 'default';
  }

  console.log(`[classifyQuery] Classifying query: "${queryText}"`);

  try {
    // Persist debug info so it survives navigation
    try {
      sessionStorage.setItem(
        'lastClassifyRequest',
        JSON.stringify({ ts: Date.now(), endpointUrl, queryText })
      );
    } catch (_) {}

    // Add a timeout so requests don't hang and to make aborts explicit in logs
    const controller = new AbortController();
    const timeoutMs = 5000; // 5s safety timeout
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Send both keys to maximize compatibility with backend expectations
      body: JSON.stringify({ text: queryText, query: queryText }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    console.log("[classifyQuery] API Response Status:", response.status);
    console.log("[classifyQuery] API Response OK:", response.ok);

    if (!response.ok) {
      console.error(`[classifyQuery] ERROR: API request failed with status: ${response.status}`);
      try {
        const errorBody = await response.text();
        console.error("[classifyQuery] API Error Body:", errorBody);
        try {
          sessionStorage.setItem(
            'lastClassifyResponse',
            JSON.stringify({ ts: Date.now(), status: response.status, ok: false, errorBody })
          );
        } catch (_) {}
      } catch (e) {
        console.error("[classifyQuery] Could not read error body.");
      }
      return 'default'; // Fallback to default if API fails
    }

    const data = await response.json();
    console.log("[classifyQuery] API Response Data:", data);
    try {
      sessionStorage.setItem(
        'lastClassifyResponse',
        JSON.stringify({ ts: Date.now(), status: response.status, ok: true, data })
      );
    } catch (_) {}

    const predictedClass = data?.class_name;
    console.log("[classifyQuery] Extracted class_name:", predictedClass);

    if (!predictedClass) {
         console.warn("[classifyQuery] Warning: 'class_name' not found in API response. Returning 'default'.");
         return 'default';
    }

    console.log(`[classifyQuery] Returning predicted class: "${predictedClass}"`);
    return predictedClass;

  } catch (error) {
    if (error?.name === 'AbortError') {
      console.error(`[classifyQuery] CATCH ERROR: Request aborted after timeout.`);
    } else {
      console.error("[classifyQuery] CATCH ERROR: Error calling classification API:", error);
    }
    try {
      sessionStorage.setItem(
        'lastClassifyError',
        JSON.stringify({ ts: Date.now(), message: String(error && error.message || error) })
      );
    } catch (_) {}
    return 'default'; // Fallback to default on network or other errors
  }
};